import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Menu, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, 
  Save, ArrowLeft, Eye, EyeOff, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { Switch } from '../components/ui/switch';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MenuAdmin = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [menuSettings, setMenuSettings] = useState({
    show_login: true,
    show_signup: true,
    logo_text: 'SENDBOX'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const { toast } = useToast();
  const { token } = useAuth();

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    loadMenuSettings();
  }, []);

  const loadMenuSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/menu`, authHeaders);
      setMenuItems(res.data.items || []);
      setMenuSettings({
        show_login: res.data.show_login,
        show_signup: res.data.show_signup,
        logo_text: res.data.logo_text || 'SENDBOX'
      });
    } catch (error) {
      console.error('Error loading menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/menu`, {
        items: menuItems,
        ...menuSettings
      }, authHeaders);
      toast({
        title: 'Menu enregistré',
        description: 'Les modifications ont été sauvegardées',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder le menu',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const addMenuItem = () => {
    const newItem = {
      id: Date.now().toString(),
      label: 'Nouveau lien',
      url: '#',
      is_dropdown: false,
      dropdown_items: [],
      is_button: false,
      button_style: 'default',
      order: menuItems.length,
      is_visible: true
    };
    setMenuItems([...menuItems, newItem]);
  };

  const updateMenuItem = (id, field, value) => {
    setMenuItems(menuItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const deleteMenuItem = (id) => {
    setMenuItems(menuItems.filter(item => item.id !== id));
  };

  const moveItem = (index, direction) => {
    const newItems = [...menuItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    newItems.forEach((item, i) => item.order = i);
    setMenuItems(newItems);
  };

  const addDropdownItem = (menuItemId) => {
    setMenuItems(menuItems.map(item => {
      if (item.id === menuItemId) {
        return {
          ...item,
          dropdown_items: [
            ...item.dropdown_items,
            { label: 'Sous-menu', url: '#' }
          ]
        };
      }
      return item;
    }));
  };

  const updateDropdownItem = (menuItemId, index, field, value) => {
    setMenuItems(menuItems.map(item => {
      if (item.id === menuItemId) {
        const newDropdownItems = [...item.dropdown_items];
        newDropdownItems[index] = { ...newDropdownItems[index], [field]: value };
        return { ...item, dropdown_items: newDropdownItems };
      }
      return item;
    }));
  };

  const deleteDropdownItem = (menuItemId, index) => {
    setMenuItems(menuItems.map(item => {
      if (item.id === menuItemId) {
        const newDropdownItems = item.dropdown_items.filter((_, i) => i !== index);
        return { ...item, dropdown_items: newDropdownItems };
      }
      return item;
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col">
      <Toaster />
      <NavigationMenu />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard?tab=settings" className="text-blue-400 hover:text-blue-300 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <Menu className="w-6 h-6 text-blue-400" />
                <h1 className="text-xl font-semibold text-white">Gestion du Menu</h1>
              </div>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Enregistrement...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" /> Enregistrer
                </span>
              )}
            </Button>
          </div>

        <div className="space-y-6">
        {/* General Settings */}
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-400" />
            Paramètres généraux
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-blue-300 mb-2">Texte du logo</label>
              <input
                type="text"
                value={menuSettings.logo_text}
                onChange={(e) => setMenuSettings({ ...menuSettings, logo_text: e.target.value })}
                className="w-full bg-slate-900/50 border border-blue-500/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-400"
              />
            </div>
            
            <div className="flex items-center justify-between bg-slate-900/30 rounded-lg px-4 py-3">
              <span className="text-sm text-blue-300">Afficher "Connexion"</span>
              <Switch
                checked={menuSettings.show_login}
                onCheckedChange={(checked) => setMenuSettings({ ...menuSettings, show_login: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between bg-slate-900/30 rounded-lg px-4 py-3">
              <span className="text-sm text-blue-300">Afficher "S'inscrire"</span>
              <Switch
                checked={menuSettings.show_signup}
                onCheckedChange={(checked) => setMenuSettings({ ...menuSettings, show_signup: checked })}
              />
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-blue-500/20 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Éléments du menu</h2>
            <Button
              onClick={addMenuItem}
              variant="outline"
              className="border-blue-500/50 text-blue-300 hover:bg-blue-500/20"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter
            </Button>
          </div>

          <div className="divide-y divide-blue-500/10">
            {menuItems
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((item, index) => (
                <div key={item.id} className="p-4">
                  {/* Main Item Row */}
                  <div className="flex items-center gap-4">
                    {/* Drag Handle & Order */}
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-blue-400/50" />
                      <div className="flex flex-col gap-1">
                        <button 
                          onClick={() => moveItem(index, 'up')}
                          disabled={index === 0}
                          className="text-blue-400/50 hover:text-blue-400 disabled:opacity-30"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => moveItem(index, 'down')}
                          disabled={index === menuItems.length - 1}
                          className="text-blue-400/50 hover:text-blue-400 disabled:opacity-30"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Visibility Toggle */}
                    <button
                      onClick={() => updateMenuItem(item.id, 'is_visible', !item.is_visible)}
                      className={`p-2 rounded-lg transition-colors ${item.is_visible ? 'text-green-400 bg-green-500/10' : 'text-red-400 bg-red-500/10'}`}
                    >
                      {item.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>

                    {/* Label */}
                    <input
                      type="text"
                      value={item.label}
                      onChange={(e) => updateMenuItem(item.id, 'label', e.target.value)}
                      className="flex-1 bg-slate-900/50 border border-blue-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                      placeholder="Label"
                    />

                    {/* URL */}
                    <input
                      type="text"
                      value={item.url}
                      onChange={(e) => updateMenuItem(item.id, 'url', e.target.value)}
                      className="w-40 bg-slate-900/50 border border-blue-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                      placeholder="URL"
                    />

                    {/* Type Toggle */}
                    <select
                      value={item.is_dropdown ? 'dropdown' : (item.is_button ? 'button' : 'link')}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateMenuItem(item.id, 'is_dropdown', val === 'dropdown');
                        updateMenuItem(item.id, 'is_button', val === 'button');
                      }}
                      className="bg-slate-900/50 border border-blue-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                    >
                      <option value="link">Lien</option>
                      <option value="dropdown">Dropdown</option>
                      <option value="button">Bouton</option>
                    </select>

                    {/* Button Style (if button) */}
                    {item.is_button && (
                      <select
                        value={item.button_style}
                        onChange={(e) => updateMenuItem(item.id, 'button_style', e.target.value)}
                        className="bg-slate-900/50 border border-blue-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                      >
                        <option value="default">Standard</option>
                        <option value="primary">Primaire</option>
                        <option value="outline">Outline</option>
                      </select>
                    )}

                    {/* Expand/Collapse Dropdown */}
                    {item.is_dropdown && (
                      <button
                        onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                        className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg"
                      >
                        {expandedItem === item.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    )}

                    {/* Delete */}
                    <button
                      onClick={() => deleteMenuItem(item.id)}
                      className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Dropdown Items */}
                  {item.is_dropdown && expandedItem === item.id && (
                    <div className="mt-4 ml-12 space-y-2 p-4 bg-slate-900/30 rounded-xl">
                      <div className="text-xs text-blue-400 font-medium mb-2">Sous-menus</div>
                      {item.dropdown_items?.map((subItem, subIndex) => (
                        <div key={subIndex} className="flex items-center gap-3">
                          <input
                            type="text"
                            value={subItem.label}
                            onChange={(e) => updateDropdownItem(item.id, subIndex, 'label', e.target.value)}
                            className="flex-1 bg-slate-800/50 border border-blue-500/30 rounded-lg px-3 py-2 text-white text-sm"
                            placeholder="Label"
                          />
                          <input
                            type="text"
                            value={subItem.url}
                            onChange={(e) => updateDropdownItem(item.id, subIndex, 'url', e.target.value)}
                            className="w-40 bg-slate-800/50 border border-blue-500/30 rounded-lg px-3 py-2 text-white text-sm"
                            placeholder="URL"
                          />
                          <button
                            onClick={() => deleteDropdownItem(item.id, subIndex)}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <Button
                        onClick={() => addDropdownItem(item.id)}
                        variant="ghost"
                        size="sm"
                        className="text-blue-400 hover:bg-blue-500/10"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Ajouter sous-menu
                      </Button>
                    </div>
                  )}
                </div>
              ))}
          </div>

          {menuItems.length === 0 && (
            <div className="p-12 text-center text-blue-300/50">
              Aucun élément de menu. Cliquez sur "Ajouter" pour créer votre premier lien.
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Aperçu</h2>
          <div className="bg-slate-900 rounded-xl p-4 border border-blue-500/20">
            <div className="flex items-center justify-between">
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                {menuSettings.logo_text}
              </span>
              <div className="flex items-center gap-4">
                {menuItems
                  .filter(item => item.is_visible)
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map(item => (
                    <span 
                      key={item.id} 
                      className={`text-sm ${item.is_button ? 'bg-blue-500 px-3 py-1 rounded-lg' : ''} ${item.is_dropdown ? 'flex items-center gap-1' : ''} text-blue-200`}
                    >
                      {item.label}
                      {item.is_dropdown && <ChevronDown className="w-3 h-3" />}
                    </span>
                  ))}
                {menuSettings.show_login && <span className="text-sm text-blue-200">Connexion</span>}
                {menuSettings.show_signup && <span className="text-sm bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-3 py-1 rounded-lg">S'inscrire</span>}
              </div>
            </div>
          </div>
        </div>
        </div>
        </div>
      </main>
      
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default MenuAdmin;
